import React, { useState } from "react";
import { auth, db } from "../utils/firebaseConfig";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { collection, addDoc, setDoc, doc } from "firebase/firestore";
import {
  Button,
  TextInput,
  PasswordInput,
  SimpleGrid,
  Alert,
  Text,
  Select,
  Center,
} from "@mantine/core";
import { MdOutlineEmail, MdPassword, MdErrorOutline } from "react-icons/md";
import { FaSignInAlt, FaRegUser } from "react-icons/fa";
import { ImCancelCircle } from "react-icons/im";
import { TiTick } from "react-icons/ti";

const SignupDrawer = ({ setIsDrawerOpen, setIsPopoverOpen }) => {
  // Hooks
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [success, setSuccess] = useState(false);

  // Function to handle signup
  const handleSignup = async () => {
    // Check if all fields are filled in
    if (
      firstName === "" ||
      lastName === "" ||
      email === "" ||
      userType === "" ||
      password === "" ||
      confirmPassword === ""
    ) {
      setErrorMessage("Please fill in all fields.");
      return;
    }

    // Check if password is at least 6 characters long
    if (password.length < 6) {
      setErrorMessage("Password must be at least 6 characters.");
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      // Register user
      const user = await createUserWithEmailAndPassword(auth, email, password);

      // Add user to users collection with a generated id
      const docRef = await addDoc(collection(db, "users"), {
        firstName: firstName,
        lastName: lastName,
        email: email,
        userType: userType,
      });

      // Set up account
      accountSetup(user.user.uid);

      setErrorMessage("");
      setSuccess(true);
      setLoading(false);
      resetFields();
    } catch (error) {
      // Handle errors
      if (error.message.includes("invalid-email")) {
        setErrorMessage("Please enter a valid email.");
      } else if (error.message.includes("email-already-in-use")) {
        setErrorMessage("Email already in use.");
      } else if (error.message.includes("weak-password")) {
        setErrorMessage("Password must be at least 6 characters.");
      } else {
        console.log(error.message);
      }
      setLoading(false);
    }
  };

  // Function to provision db collection for user
  const accountSetup = async (userId) => {
    // Create a collection for user with todos document
    const docRef = await setDoc(doc(db, userId, "todos"), {
      todos: [{ todo: "Add a new todo", completed: false }],
    });
  };

  // Function to reset fields
  const resetFields = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setUserType("");
    setPassword("");
    setConfirmPassword("");
  };

  // Function to siwtch to login popover
  const switchToLogin = () => {
    setIsDrawerOpen(false);
    setIsPopoverOpen(true);
  };

  return (
    <div>
      <TextInput
        icon={<FaRegUser />}
        placeholder="First Name"
        label="First Name"
        required
        style={{ marginBottom: 10 }}
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
      />
      <TextInput
        icon={<FaRegUser />}
        placeholder="Last Name"
        label="Last Name"
        required
        style={{ marginBottom: 10 }}
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
      />
      <TextInput
        type="email"
        icon={<MdOutlineEmail />}
        placeholder="Email"
        label="Email"
        required
        style={{ marginBottom: 10 }}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Select
        label="Signup As"
        placeholder="Choose one"
        required
        style={{ marginBottom: 10 }}
        data={[
          { value: "doctor", label: "Doctor" },
          { value: "patient", label: "Patient" },
        ]}
        value={userType}
        onChange={(e) => setUserType(e)}
      />
      <PasswordInput
        icon={<MdPassword />}
        placeholder="Password"
        label="Password"
        description="Password must be at least 6 characters long"
        required
        style={{ marginBottom: 15 }}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <PasswordInput
        icon={<MdPassword />}
        placeholder="Confirm Password"
        label="Confirm Password"
        required
        style={{ marginBottom: 25 }}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />
      <SimpleGrid cols={2} style={{ marginBottom: 10 }}>
        <Button
          leftIcon={<FaSignInAlt />}
          onClick={handleSignup}
          color="indigo"
          loading={loading}
        >
          Register
        </Button>
        <Button
          leftIcon={<ImCancelCircle />}
          color="indigo"
          variant="outline"
          onClick={() => setIsDrawerOpen(false)}
        >
          Cancel
        </Button>
      </SimpleGrid>
      <Center>
        <Text color="dimmed" size="xs">
          Already have an account?{" "}
          <span
            onClick={switchToLogin}
            style={{
              fontWeight: 700,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Login
          </span>{" "}
        </Text>
      </Center>

      {errorMessage !== "" && (
        <Alert
          icon={<MdErrorOutline />}
          title="ERROR"
          color="red"
          style={{ marginTop: 20 }}
          variant="filled"
        >
          {errorMessage}
        </Alert>
      )}

      {success && (
        <Alert
          icon={<TiTick />}
          title="SUCCESS"
          color="green"
          style={{ marginTop: 20 }}
          variant="filled"
        >
          Successfully created account. Click{" "}
          <span
            onClick={switchToLogin}
            style={{
              fontWeight: 700,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            here
          </span>{" "}
          to login.
        </Alert>
      )}
    </div>
  );
};

export default SignupDrawer;
